import { DateValue, parseDate } from '@internationalized/date';
import {
  enumSalesOrderSortOption,
  enumSalesOrderStatus,
  LoadStatus,
  SalesOrderSortOption,
  SalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridSelection } from '@rematter/ui-web';
import { atom } from 'jotai';

import { salesOrderBulkVoidAtom } from '../_graphql/mutationSalesOrderBulkVoid';
import { salesOrderDeleteAtom } from '../_graphql/mutationSalesOrderDelete';
import { salesOrderUpdateAtom } from '../_graphql/mutationSalesOrderUpdate';
import { salesOrderVoidAtom } from '../_graphql/mutationSalesOrderVoid';
import { salesOrdersListQueryAtom } from '../_graphql/querySalesOrdersList';
import { SalesOrderRecord } from '../types/salesOrder';

import { atomFromQueryString } from '~/shared/state';
import { anyDateToISOString } from '~/shared/utils/dateTime';

interface AffectedLoad {
  id: string;
  documentNumber: string;
  status: LoadStatus;
}

export interface SalesOrderVoidValidation {
  salesOrderId: string;
  canVoid: boolean;
  requiresForceUnlink: boolean;
  blockingLoads: AffectedLoad[];
  unlinkableLoads: AffectedLoad[];
  currentStatus?: SalesOrderStatus;
}

// 🟣🟣🟣 PARAMS
export const $searchTerm = atomFromQueryString.string('q');
export const $selectedFacility = atomFromQueryString.string('f');
export const $limit = atomFromQueryString.number('limit', 25);
export const $offset = atomFromQueryString.number('offset', 0);
export const $selectedCustomerIds =
  atomFromQueryString.stringSet('customerIds');
export const $selectedCreatedByUserIds =
  atomFromQueryString.stringSet('createdByIds');

// 🟣🟣🟣 COLUMN FILTER PARAMS
export const $columnDateRange = atomFromQueryString.dateRange();
export const $columnCreatedOnDateRange = atomFromQueryString.custom<{
  start: DateValue;
  end: DateValue;
} | null>('createdOnRange', {
  parse: (value) => {
    if (!value) return null;
    try {
      const [startStr, endStr] = value.split('..');
      if (!startStr || !endStr) return null;

      const start = parseDate(startStr);
      const end = parseDate(endStr);

      return { start, end };
    } catch {
      return null;
    }
  },
  serialize: (value) => {
    if (!value || !value.start || !value.end) return undefined;

    return `${value.start.toString()}..${value.end.toString()}`;
  },
  defaultValue: undefined,
});
export const $columnSelectedStatuses =
  atomFromQueryString.stringSet<SalesOrderStatus>('colStatuses');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedKeys = atom<DataGridSelection>(new Set<React.Key>());
export const $isWizardOpen = atom<boolean>(false);
export const $draftSalesOrderId = atom<string | null>(null);
export const $isLoadDialogOpen = atom<boolean>(false);
export const $selectedSalesOrder = atom<SalesOrderRecord | null>(null);
export const $isVoidDialogOpen = atom<boolean>(false);
export const $isCloseDialogOpen = atom<boolean>(false);
export const $currentSorting = atom<SalesOrderSortOption[]>([
  enumSalesOrderSortOption.CreatedAtDesc,
]);

// Bulk void dialog state
type SalesOrder = {
  id: string;
  displayNumber: string;
  status: SalesOrderStatus;
};
export const $isBulkVoidDialogOpen = atom<boolean>(false);
export const $bulkVoidSalesOrders = atom<SalesOrder[]>([]);

// 🟢🟢🟢 REMOTE DATA
export const $remoteSalesOrders = salesOrdersListQueryAtom((get) => {
  const searchTerm = get($searchTerm);
  const selectedFacility = get($selectedFacility);
  const limit = get($limit);
  const offset = get($offset);
  const accountIds = Array.from(get($selectedCustomerIds)).map((id) =>
    id.toString(),
  );
  const createdByUserIds = Array.from(get($selectedCreatedByUserIds));

  // Column filters
  const columnSelectedStatuses = get($columnSelectedStatuses);
  const columnDateRange = get($columnDateRange);
  const columnCreatedOnDateRange = get($columnCreatedOnDateRange);
  const sortingParams = get($currentSorting);

  const params: {
    searchTerm?: string;
    facilityId?: string;
    limit: number;
    offset: number;
    sorting: SalesOrderSortOption[];
    includeLineItems?: boolean;
    accountIds?: string[];
    createdByUserIds?: string[];
    status?: SalesOrderStatus[];
    fromEndDate?: string;
    toEndDate?: string;
    fromCreatedAt?: string;
    toCreatedAt?: string;
  } = {
    limit,
    offset,
    sorting: sortingParams,
    includeLineItems: true,
    accountIds,
  };

  if (createdByUserIds.length > 0) {
    params.createdByUserIds = createdByUserIds;
  }

  if (searchTerm) params.searchTerm = searchTerm;
  if (selectedFacility && selectedFacility !== 'all')
    params.facilityId = selectedFacility;

  // Column filters
  if (columnSelectedStatuses.size > 0) {
    params.status = Array.from(columnSelectedStatuses);
  }

  if (columnDateRange?.start) {
    params.fromEndDate = anyDateToISOString(columnDateRange.start);
  }
  if (columnDateRange?.end) {
    params.toEndDate = anyDateToISOString(columnDateRange.end);
  }

  if (columnCreatedOnDateRange?.start) {
    params.fromCreatedAt = anyDateToISOString(columnCreatedOnDateRange.start);
  }
  if (columnCreatedOnDateRange?.end) {
    params.toCreatedAt = anyDateToISOString(columnCreatedOnDateRange.end);
  }

  return {
    variables: params,
  };
});

// 🟠🟠🟠 ACTION -> DELETE SALES ORDER
export const $deleteSalesOrder = salesOrderDeleteAtom({
  onSuccess: (get) => {
    // Refetch sales orders after successful delete
    get($remoteSalesOrders).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> UPDATE SALES ORDER (for bulk operations)
export const $updateSalesOrder = salesOrderUpdateAtom({
  onSuccess: (get) => {
    get($remoteSalesOrders).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> VOID SALES ORDER
export const $voidSalesOrder = salesOrderVoidAtom({
  onSuccess: (get) => {
    get($remoteSalesOrders).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> BULK VOID SALES ORDER
export const $bulkVoidSalesOrder = salesOrderBulkVoidAtom({
  onSuccess: (get) => {
    get($remoteSalesOrders).refetch?.();
  },
});

// 🟢🟢🟢 HELPER -> Create fetch validation function for VoidEntityDialog
// This calls the bulk void mutation with forceUnlink=false to validate
export const createSalesOrdersVoidValidationFetcher =
  (
    bulkVoidMutation: (params: {
      ids: string[];
      forceUnlink?: boolean;
    }) => Promise<{
      tradeSalesOrderBulkVoid: {
        results: Array<{
          salesOrder: { id: string; status: SalesOrderStatus } | null;
          blockingLoads: Array<{
            id: string;
            documentNumber: string;
            status: LoadStatus;
          }> | null;
          unlinkableLoads: Array<{
            id: string;
            documentNumber: string;
            status: LoadStatus;
          }> | null;
          requiresConfirmation: boolean;
        }>;
        successCount: number;
        failedCount: number;
      };
    }>,
  ) =>
  async (
    salesOrderIds: string[],
  ): Promise<Map<string, SalesOrderVoidValidation>> => {
    const results = new Map<string, SalesOrderVoidValidation>();

    if (salesOrderIds.length === 0) {
      return results;
    }

    const response = await bulkVoidMutation({
      ids: salesOrderIds,
      forceUnlink: false,
    });

    // Results are in same order as input IDs, use index to correlate
    response.tradeSalesOrderBulkVoid.results.forEach((item, index) => {
      const salesOrderId = salesOrderIds[index];

      const blockingLoads: AffectedLoad[] = (item.blockingLoads ?? []).map(
        (load) => ({
          id: load.id,
          documentNumber: load.documentNumber,
          status: load.status,
        }),
      );

      const unlinkableLoads: AffectedLoad[] = (item.unlinkableLoads ?? []).map(
        (load) => ({
          id: load.id,
          documentNumber: load.documentNumber,
          status: load.status,
        }),
      );

      const hasBlockingLoads = blockingLoads.length > 0;
      const hasUnlinkableLoads = unlinkableLoads.length > 0;
      const currentStatus = item.salesOrder?.status;
      const isAlreadyVoided = currentStatus === enumSalesOrderStatus.Voided;

      results.set(salesOrderId, {
        salesOrderId,
        canVoid: !hasBlockingLoads && !isAlreadyVoided,
        requiresForceUnlink: hasUnlinkableLoads && !hasBlockingLoads,
        blockingLoads,
        unlinkableLoads,
        currentStatus,
      });
    });

    return results;
  };
